"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useUser } from "@clerk/nextjs";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { createClientTtlCache } from "@/lib/client-ttl-cache";
import { gamjaExamSlugFromPath } from "@/lib/gamja-exam-completion";
import { getGamjaExamCompletionCounts } from "@/lib/server-actions/gamjaExamCompletionActions";

// 과목 페이지 카드와 감자시험 페이지가 같은 스냅샷(경로 → 도전 횟수)을 공유한다(60초 TTL, JCG-3865).
// 도전을 기록하면 applyGamjaExamCompletionCount 로 즉시 반영해 목록으로 돌아와도 최신 횟수를 보여준다.
const countsCache = createClientTtlCache<Record<string, number>>(async () => {
  const result = await getGamjaExamCompletionCounts();
  return result.success ? result.counts : null;
}, 60_000);
// 캐시를 채운 로그인 사용자. 같은 탭에서 계정이 바뀌면 이전 사용자의 횟수를 비운다.
let cachedUserId: string | null = null;

/**
 * 도전을 기록한 뒤 서버가 돌려준 횟수를 캐시에 반영한다 (JCG-3865).
 * 목록을 아직 불러오지 못했으면(불러오는 중이면 기록 전 값일 수 있음) 비우고 새로 불러온다.
 */
export function applyGamjaExamCompletionCount(slug: string, count: number): void {
  const updated = countsCache.update((counts) => ({ ...counts, [slug]: count }));
  if (!updated && cachedUserId) {
    countsCache.clear();
    void countsCache.load();
  }
}

/**
 * 감자시험 도전 횟수 로드 훅 (Premium 이상만 표시). JCG-3865
 *
 * 감자시험 카드(ExamButtons·KingGamjaExam)와 감자시험 페이지가 공유한다.
 * 비프리미엄/비로그인/오류 시 showCompletion=false 로 아무것도 표시하지 않는다.
 */
export function useGamjaExamCompletion() {
  const { isPremiumOrAbove } = useEffectivePass();
  const { user } = useUser();
  const userId = user?.id ?? null;
  const counts = useSyncExternalStore(
    countsCache.subscribe,
    countsCache.getSnapshot,
    () => null,
  );

  useEffect(() => {
    if (!userId || !isPremiumOrAbove) return;
    if (cachedUserId !== userId) {
      countsCache.clear();
      cachedUserId = userId;
    }
    void countsCache.load();
  }, [isPremiumOrAbove, userId]);

  // 로드 전에는 표시하지 않는다(0회 안내가 잠깐 보였다 사라지는 깜빡임 방지).
  const showCompletion =
    isPremiumOrAbove && userId !== null && counts !== null && cachedUserId === userId;

  /** 감자시험 경로·href 의 도전 횟수 (0 = 아직 없음 / 감자시험 아님). */
  const completionForHref = (href: string): number => {
    const slug = gamjaExamSlugFromPath(href);
    return slug && counts ? (counts[slug] ?? 0) : 0;
  };

  return { showCompletion, completionForHref };
}
